import type { KyInstance } from 'ky';

import { ensureError, formatString, isNotDefined } from '@enterprise/core';
import type { OperationResult } from '@enterprise/types';

import { Uri } from '#constants/uri';

/**
 * Deletes a template detail by its ID.
 *
 * Sends a DELETE request to the API endpoint for deleting a template detail,
 * using the provided KyInstance and template detail ID.
 *
 * @param instance - The KyInstance used to make the HTTP request.
 * @param templateId - The unique identifier of the template that the detail belongs to.
 * @param templateDetailId - The unique identifier of the template detail to delete.
 * @returns A promise that resolves to an OperationResult indicating the outcome of the operation.
 * @throws Throws an error if the request fails.
 */
export const deleteTemplateDetailById = async (
  instance: KyInstance,
  templateId?: number,
  templateDetailId?: number
) => {
  try {
    if (isNotDefined(templateId) || isNotDefined(templateDetailId)) {
      throw new Error('Template ID and Template detail ID must be provided for deletion.');
    }

    return await instance
      .delete<OperationResult>(
        formatString(Uri.template.deleteTemplateDetailById, templateId.toString(), templateDetailId.toString())
      )
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
